import type { Atom } from "#public"
import type { Sequence } from "#public"

import { RecordMap } from "./RecordMap"

/**
 * Groups items of a sequence by key, optionally combining each group.
 *
 * @param sequence - Sequence to group
 * @param func - Key function (receives item and index)
 * @param combiner - Optional function to combine each group's items
 * @returns Record from stringified key to its group (or combined value), in first-seen key order
 *
 * @example
 * ```ts
 * import { GroupBy } from "@pdfking/tfunc"
 *
 * GroupBy([1, 2, 3, 4], x => (x % 2 === 0 ? "even" : "odd"))
 * // { odd: [1, 3], even: [2, 4] }
 *
 * GroupBy([1, 2, 3, 4], x => x % 2, items => items.length)
 * // { 1: 2, 0: 2 }
 * ```
 */
export const GroupBy = <T, K extends Atom, N = T[]>(
  sequence: Sequence<T>,
  func: (item: T, index: number) => K,
  combiner?: (items: T[]) => N
): Record<string, N> => {
  const groups: Record<string, T[]> = {}
  let index = 0

  for (const item of sequence) {
    const key = `${func(item, index++)}`
    const group = groups[key]
    if (group) group.push(item)
    else groups[key] = [item]
  }

  return combiner ? RecordMap(combiner, groups) : (groups as Record<string, N>)
}
